// src/context/MyProvidersContext.js
// Global context for the user's uploaded provider lists.
// Supports multi-list selection for filter and future comparison features.
// Manages BigQuery sync state — triggers on-demand sync when filter activated.

import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { useUser } from "@clerk/react";
import { useSubscription } from "./SubscriptionContext";
import { apiFetch } from "../utils/apiClient";

const MyProvidersContext = createContext(null);

export function MyProvidersProvider({ children }) {
  const { user, isSignedIn } = useUser();
  const { isPro } = useSubscription();

  const [lists, setLists] = useState([]);
  const [selectedListIds, setSelectedListIds] = useState(new Set()); // multi-select
  const [providerNpis, setProviderNpis] = useState(new Set());
  const [filterActive, setFilterActive] = useState(false);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false); // BQ sync in progress
  const [syncError, setSyncError] = useState(null);

  // ── Load lists when user signs in ─────────────────────────────────────────
  useEffect(() => {
    if (!isSignedIn || !user || !isPro) {
      setLists([]);
      setSelectedListIds(new Set());
      setProviderNpis(new Set());
      setFilterActive(false);
      return;
    }
    loadLists();
  }, [isSignedIn, user?.id, isPro]);

  const loadLists = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await apiFetch(`/api/user-data?action=getProviderLists&userId=${user.id}`);
      const data = await res.json();
      const fetchedLists = data.lists || [];
      setLists(fetchedLists);
      // Do NOT auto-select — let the user choose which lists to filter
      // This prevents conflicts with manual checkbox selection
    } catch {
      setLists([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id]);

  // ── Load NPIs for all selected lists ──────────────────────────────────────
  const loadProviderNpis = useCallback(async (listIds) => {
    if (!user || listIds.size === 0) {
      setProviderNpis(new Set());
      return;
    }
    try {
      const allNpis = new Set();
      await Promise.all([...listIds].map(async (listId) => {
        const res = await apiFetch(
          `/api/user-data?action=getProviderNpis&userId=${user.id}&listId=${listId}`
        );
        const data = await res.json();
        (data.npis || []).forEach((npi) => allNpis.add(String(npi)));
      }));
      setProviderNpis(allNpis);
    } catch {
      setProviderNpis(new Set());
    }
  }, [user?.id]);

  // ── Toggle a list in/out of selection ─────────────────────────────────────
  const toggleListSelection = useCallback((listId) => {
    setSelectedListIds((prev) => {
      const next = new Set(prev);
      if (next.has(listId)) {
        next.delete(listId);
      } else {
        next.add(listId);
      }
      return next;
    });
  }, []);

  const selectOnlyList = useCallback((listId) => {
    setSelectedListIds(new Set([listId]));
  }, []);

  const selectAllLists = useCallback(() => {
    setSelectedListIds(new Set(lists.map((l) => l.id)));
  }, [lists]);

  const deselectAllLists = useCallback(() => {
    setSelectedListIds(new Set());
  }, []);

  // ── Ensure selected lists are BQ-synced, then activate filter ─────────────
  const activateFilter = useCallback(async () => {
    if (!user || selectedListIds.size === 0) return;
    setSyncError(null);

    // Find lists that need syncing
    const listsToSync = lists.filter(
      (l) => selectedListIds.has(l.id) && !l.bq_synced
    );

    if (listsToSync.length > 0) {
      setSyncing(true);
      try {
        await Promise.all(
          listsToSync.map((list) =>
            apiFetch("/api/sync-providers-bq", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ userId: user.id, listId: list.id }),
            }).then(async (res) => {
              if (!res.ok) throw new Error(`Sync failed for ${list.list_name}`);
              // Update local list state to bq_synced: true
              setLists((prev) =>
                prev.map((l) => l.id === list.id ? { ...l, bq_synced: true } : l)
              );
            })
          )
        );
      } catch (err) {
        setSyncError(err.message || "Sync failed. Please try again.");
        setSyncing(false);
        return;
      }
      setSyncing(false);
    }

    // Load NPIs for filter badge display (lightweight — used for "X providers in list" label)
    await loadProviderNpis(selectedListIds);
    setFilterActive(true);
  }, [user?.id, selectedListIds, lists, loadProviderNpis]);

  const deactivateFilter = useCallback(() => {
    setFilterActive(false);
  }, []);

  const toggleFilter = useCallback(async () => {
    if (filterActive) {
      deactivateFilter();
    } else {
      await activateFilter();
    }
  }, [filterActive, activateFilter, deactivateFilter]);

  const refreshLists = useCallback(() => loadLists(), [loadLists]);

  // Active list metadata for display
  const selectedLists = lists.filter((l) => selectedListIds.has(l.id));
  const totalSelectedProviders = selectedLists.reduce(
    (s, l) => s + (l.provider_count || 0), 0
  );

  // Comma-separated listIds string for BigQuery queries
  const selectedListIdsString = [...selectedListIds].join(",");

  return (
    <MyProvidersContext.Provider
      value={{
        lists,
        selectedListIds,
        selectedLists,
        selectedListIdsString,
        providerNpis,
        filterActive,
        loading,
        syncing,
        syncError,
        totalSelectedProviders,
        toggleListSelection,
        selectOnlyList,
        selectAllLists,
        deselectAllLists,
        toggleFilter,
        activateFilter,
        deactivateFilter,
        refreshLists,
        // Legacy single-list compat — used by MyProvidersPage table load
        activeList: selectedLists[0] || null,
        setActiveList: (list) => selectOnlyList(list.id),
        // Convenience check
        isMyProvider: (npi) => providerNpis.has(String(npi)),
        // For BigQuery filter queries
        myProviderNpiArray: filterActive ? [...providerNpis] : null,
      }}
    >
      {children}
    </MyProvidersContext.Provider>
  );
}

export function useMyProviders() {
  return useContext(MyProvidersContext);
}